import React from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import PropTypes from "prop-types";

// Custom hooks
import useMemberData from "layouts/tables/data/singleMemberTableData";
import Grid from "@mui/material/Grid";
import { API_HOST } from "config/api";

// Helper component for detail items
const DetailItem = ({ label, value, icon }) => (
  <Box
    mb={2}
    p={2}
    sx={{
      backgroundColor: "rgba(0,0,0,0.02)",
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.1)",
    }}
  >
    <Box display="flex" alignItems="center" mb={0.5}>
      {icon && <Icon sx={{ mr: 1, color: "info.main" }}>{icon}</Icon>}
      <Typography variant="caption" fontWeight={700} color="info">
        {label}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ pl: icon ? 4 : 0 }}>
      {value || "Not provided"}
    </Typography>
  </Box>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  icon: PropTypes.string,
};

function Member() {
  const { isLoading, error, selectedMember, MemberDetailsModal, showModalOpen, setShowModalOpen } =
    useMemberData();

  if (isLoading) {
    return (
      <Box pt={6} pb={3} display="flex" justifyContent="center">
        <Typography>Loading your member profile...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3} display="flex" justifyContent="center">
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  if (!selectedMember) {
    return (
      <Box pt={6} pb={3} display="flex" justifyContent="center">
        <Typography>No member data found.</Typography>
      </Box>
    );
  }

  return (
    <Box pt={6} pb={3}>
      <Card>
        <Box
          mx={2}
          mt={-3}
          py={3}
          px={2}
          sx={{
            background: (theme) =>
              `linear-gradient(195deg, ${theme.palette.info.light}, ${theme.palette.info.main})`,
            borderRadius: "12px",
            boxShadow: 3,
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h6" sx={{ color: "white" }}>
                My Member Profile
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={() => setShowModalOpen(true)}
                startIcon={<Icon>visibility</Icon>}
              >
                View Full Details
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box p={4}>
          {/* Member Header with Photo */}
          <Box
            display="flex"
            alignItems="center"
            mb={4}
            p={3}
            sx={{
              backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "12px",
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
            }}
          >
            {selectedMember.photo ? (
              <Box
                component="img"
                src={`${API_HOST}/storage/${selectedMember.photo}`}
                alt="Member"
                sx={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  mr: 3,
                  border: "4px solid white",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                }}
              />
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  backgroundColor: "info.main",
                  mr: 3,
                  color: "white",
                }}
              >
                <Icon sx={{ fontSize: "3rem" }}>person</Icon>
              </Box>
            )}
            <Box>
              <Typography variant="h4" fontWeight={700} mb={1}>
                {selectedMember.firstName} {selectedMember.middleName} {selectedMember.surname}
              </Typography>
              <Box display="flex" alignItems="center" mb={1}>
                <Icon sx={{ mr: 1, color: "info.main" }}>badge</Icon>
                <Typography variant="h6" color="info">
                  ID: {selectedMember.memberId || "Not assigned"}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Icon
                  sx={{
                    mr: 1,
                    color: selectedMember.status === "active" ? "success.main" : "error.main",
                  }}
                >
                  {selectedMember.status === "active" ? "check_circle" : "cancel"}
                </Icon>
                <Typography
                  variant="button"
                  color={selectedMember.status === "active" ? "success" : "error"}
                  fontWeight={700}
                >
                  {selectedMember.status?.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Basic Information Section */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(102, 126, 234, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(102, 126, 234, 0.2)",
                }}
              >
                <Typography variant="h5" fontWeight={700} mb={3} color="info">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>person_outline</Icon>
                  Personal Information
                </Typography>
                <DetailItem label="First Name" value={selectedMember.firstName} icon="person" />
                <DetailItem label="Middle Name" value={selectedMember.middleName} icon="person" />
                <DetailItem label="Surname" value={selectedMember.surname} icon="person" />
                <DetailItem label="Gender" value={selectedMember.gender} icon="wc" />
                <DetailItem label="Date of Birth" value={selectedMember.dateOfBirth} icon="cake" />
                <DetailItem
                  label="Marital Status"
                  value={selectedMember.maritalStatus}
                  icon="favorite"
                />
                <DetailItem label="Phone Number" value={selectedMember.phoneNumber} icon="phone" />
                <DetailItem
                  label="WhatsApp Number"
                  value={selectedMember.whatsappNumber}
                  icon="chat"
                />
                <DetailItem label="Email" value={selectedMember.email} icon="email" />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(76, 175, 80, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(76, 175, 80, 0.2)",
                }}
              >
                <Typography variant="h5" fontWeight={700} mb={3} color="success">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>location_on</Icon>
                  Address Information
                </Typography>
                <DetailItem
                  label="Residential Address"
                  value={selectedMember.residentialAddress}
                  icon="home"
                />
                <DetailItem
                  label="Postal Address"
                  value={selectedMember.postalAddress}
                  icon="mail"
                />
                <DetailItem label="Region" value={selectedMember.region} icon="public" />
                <DetailItem
                  label="Home Town"
                  value={selectedMember.homeTown}
                  icon="location_city"
                />
                <DetailItem label="Nationality" value={selectedMember.nationality} icon="flag" />
                <DetailItem
                  label="Place of Birth"
                  value={selectedMember.placeOfBirth}
                  icon="place"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Full Details Modal */}
      <MemberDetailsModal
        member={selectedMember}
        open={showModalOpen}
        onClose={() => setShowModalOpen(false)}
      />
    </Box>
  );
}

Member.propTypes = {
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  selectedMember: PropTypes.object,
  showModalOpen: PropTypes.bool,
  setShowModalOpen: PropTypes.func,
};

export default Member;
